import React, { useState } from "react";
import DataServiceSQL from "../../DataService/DataServiceSQL";
import { Row, Col } from 'reactstrap';
import { withStyles } from '@material-ui/core';
import * as Constants from '../../Pages/Constants';
import InvalidObject from '../Common/InvalidObject.js';
import { FormattedMessage, useIntl } from 'react-intl';

const AddUser = (props) => {
  const isSuperAdmin = props.accountInformation
    && props.accountInformation.role === "admin"
    && props.accountInformation.role_extra === "admin_etc";

  const [user, setUser] = useState({ user_mail: "" });
  const [civility, setCivility] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [language, setLanguage] = useState("fr-FR");
  const [role, setRole] = useState("registered");
  const [adminEtc, setAdminEtc] = useState(false);
  const [sendInvitation, setSendInvitation] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [message, setMessage] = useState("");
  const [isBeingCreated, setIsBeingCreated] = useState(false);
  const [invalidObject, setInvalidObject] = useState(false);
  const [emailHintVisible, setEmailHintVisible] = useState(false);

  const intl = useIntl();

  const isValidEmail = (email) => {
    const trimmedEmail = (email || "").trim();
    const checkExp = new RegExp('^[a-z0-9]+([_|\\.|-]{1}[a-z0-9]+)*@[a-z0-9]+([_|\\.|-]{1}[a-z0-9]+)*[\\.]{1}[a-z]{2,6}$', 'i');
    return checkExp.test(trimmedEmail);
  };

  const hasValidEmail = isValidEmail(user.user_mail);

  React.useEffect(() => { if (hasValidEmail) setEmailHintVisible(false); }, [hasValidEmail]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setUser({ ...user, [name]: value });
  };

  const handleRoleChange = (value) => {
    setRole(value);
    if (value !== "admin") {
      setAdminEtc(false);
    }
  };

  const cancelInvalidObjectPopup = () => {
    setInvalidObject(false);
  };

  const getRequestErrorMessage = (error) => {
    if (error && error.response && error.response.data) {
      if (typeof (error.response.data) === "string") {
        return error.response.data;
      }
      return JSON.stringify(error.response.data);
    }
    if (error && error.message) {
      return error.message;
    }
    return "";
  };

  const getTranslatedErrorMessage = (errorMessage) => {
    if (!errorMessage) {
      return <FormattedMessage id="message.error.addUser" defaultMessage="Oops, that didn't work... (err_code addUser)" />;
    }
    if (errorMessage.includes("User already exist")) {
      return <FormattedMessage id="message.error.userAlreadyExists" defaultMessage="User already exists" />;
    }
    if (errorMessage.includes("Can't create user in local database")) {
      return <FormattedMessage id="staff.error.localDatabase" defaultMessage="Unable to create the user in the local database" />;
    }
    if (errorMessage.includes("Can't create user in Keycloak")
      || errorMessage.includes("Keycloak user creation error")
      || errorMessage.includes("Keycloak admin token error")
      || errorMessage.includes("Keycloak user search error")) {
      return <FormattedMessage id="staff.error.identityProvider" defaultMessage="Unable to create the user account" />;
    }
    if (errorMessage.includes("Wrong user rights")) {
      return <FormattedMessage id="staff.error.forbidden" defaultMessage="You do not have permission to add a user" />;
    }
    return <FormattedMessage id="message.error.addUser" defaultMessage="Oops, that didn't work... (err_code addUser)" />;
  };

  const saveUser = async () => {
    if (!isSuperAdmin) {
      setMessage(<FormattedMessage id="staff.error.forbidden" defaultMessage="You do not have permission to add a user" />);
      setInvalidObject(true);
      return;
    }
    if (!hasValidEmail) {
      setMessage(<FormattedMessage id="message.invalidMail" defaultMessage="Invalid email" />);
      setInvalidObject(true);
      return;
    }
    if (!firstName.trim() || !lastName.trim()) {
      setMessage(<FormattedMessage id="patient.create.wrongEntry" defaultMessage="Last name and first name are required" />);
      setInvalidObject(true);
      return;
    }

    setMessage(<FormattedMessage id="user.creation" defaultMessage="User creation, please wait..." />);
    setIsBeingCreated(true);

    const nameEntry = {
      "use": "official",
      "family": lastName.trim(),
      "given": [firstName.trim()]
    };
    if (civility) {
      nameEntry.prefix = [civility];
    }

    const data_create = {
      "resourceType": "Practitioner",
      "identifier": { "value": user.user_mail },
      "name": [nameEntry],
      "telecom": {
        "system": "email",
        "value": user.user_mail
      },
      "send_invitation": sendInvitation,
      "language": language || intl.locale,
      "FK_structure": props.targetStructureId
    };

    // After creation, user is "registered" by default. Promote if needed.
    const data_update = {
      "resourceType": "Practitioner",
      "FK_structure": props.targetStructureId
    };
    if (role !== "registered") {
      data_update.role = role;
    }
    if (role === "admin" && adminEtc) {
      data_update.role_extra = "admin_etc";
    }

    try {
      await DataServiceSQL.createUserReq_fhir(data_create);

      // Only call PUT if there's something to update beyond the default
      if (role !== "registered" || (role === "admin" && adminEtc)) {
        await DataServiceSQL.updatePractitionnerReq_fhir(user.user_mail, data_update);
      }

      setMessage(<FormattedMessage id="message.success" defaultMessage="Success !" />);
      setSubmitted(true);
    } catch (e) {
      const errorMessage = getRequestErrorMessage(e);
      const translatedErrorMessage = getTranslatedErrorMessage(errorMessage);
      setMessage(
        <>
          <FormattedMessage id="message.error.addUser" defaultMessage="Oops, that didn't work... (err_code addUser)" />
          {errorMessage !== "" ? <><br /><span style={{ fontSize: "0.9em" }}>{translatedErrorMessage}</span></> : null}
        </>
      );
      setSubmitted(true);
    }
  };

  const { classes } = props;

  if (!isSuperAdmin) {
    return (
      <div className={classes.popup_background}>
        <div style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div className="submit-form">
            <center>
              <h4><FormattedMessage id="staff.error.forbidden" defaultMessage="You do not have permission to add a user" /></h4>
              <button onClick={props.cancelPopup} className="btn btn-danger">
                <FormattedMessage id="button.close" defaultMessage="Close" />
              </button>
            </center>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.popup_background}>
      <div style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
        <div className="submit-form">
          {submitted ? (
            <div>
              <center>
                <Row className="justify-content-md-end">
                  <Col style={{ fontWeight: "bold", fontSize: "2.2em" }}>
                    <FormattedMessage id="user.add" defaultMessage="Add user" />
                  </Col>
                </Row>
                <h4>{message}</h4>
                <button onClick={props.closeAfterAdd} className="btn btn-success">
                  <FormattedMessage id="button.close" defaultMessage="Close" />
                </button>
              </center>
            </div>
          ) : (
            <div>
              <center>
                <div>
                  <Row className="justify-content-md-end">
                    <Col style={{ fontWeight: "bold", fontSize: "2.0em" }}>
                      <FormattedMessage id="user.add" defaultMessage="Add user" />
                    </Col>
                  </Row>
                </div>
                <br />
                {isBeingCreated ? (
                  <div>
                    <center>
                      <h4>{message}</h4>
                    </center>
                  </div>
                ) : (
                  <div>
                    <div className="form-group" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "16px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <input
                          type="radio"
                          id="role_registered"
                          name="user_role"
                          value="registered"
                          checked={role === "registered"}
                          onChange={() => handleRoleChange("registered")}
                        />
                        <label htmlFor="role_registered" style={{ marginBottom: 0 }}>
                          <FormattedMessage id="user.role.registered" defaultMessage="Registered" />
                        </label>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <input
                          type="radio"
                          id="role_staff"
                          name="user_role"
                          value="staff"
                          checked={role === "staff"}
                          onChange={() => handleRoleChange("staff")}
                        />
                        <label htmlFor="role_staff" style={{ marginBottom: 0 }}>
                          <FormattedMessage id="user.role.staff" defaultMessage="Staff" />
                        </label>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <input
                          type="radio"
                          id="role_admin"
                          name="user_role"
                          value="admin"
                          checked={role === "admin"}
                          onChange={() => handleRoleChange("admin")}
                        />
                        <label htmlFor="role_admin" style={{ marginBottom: 0 }}>
                          <FormattedMessage id="user.role.admin" defaultMessage="Admin" />
                        </label>
                      </div>
                    </div>
                    {role === "admin" && (
                      <div className="form-group" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                        <input
                          type="checkbox"
                          id="admin_etc"
                          checked={adminEtc}
                          onChange={(e) => setAdminEtc(e.target.checked)}
                        />
                        <label htmlFor="admin_etc" style={{ marginBottom: 0 }}>
                          <FormattedMessage id="user.role.admin_etc" defaultMessage="Set as super admin (admin_etc)" />
                        </label>
                      </div>
                    )}
                    <br />
                    <div style={{ maxWidth: "420px", margin: "0 auto", textAlign: "left" }}>
                      <div className="form-group">
                        <label htmlFor="user_civility">
                          <FormattedMessage id="account.civility" defaultMessage="Civility" />
                        </label>
                        <select
                          className="form-control"
                          id="user_civility"
                          value={civility}
                          onChange={(e) => setCivility(e.target.value)}
                        >
                          <option value="">--</option>
                          <option value="Mr">Mr</option>
                          <option value="Ms">Ms</option>
                          <option value="Dr">Dr</option>
                          <option value="Prof.">Prof.</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label htmlFor="user_lastname">
                          <FormattedMessage id="account.lastname" defaultMessage="Last name" /> <span style={{ color: "red" }}>*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="user_lastname"
                          required
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="user_firstname">
                          <FormattedMessage id="account.firstname" defaultMessage="First name" /> <span style={{ color: "red" }}>*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="user_firstname"
                          required
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="user_language">
                          <FormattedMessage id="account.language" defaultMessage="Language" />
                        </label>
                        <select
                          className="form-control"
                          id="user_language"
                          value={language}
                          onChange={(e) => setLanguage(e.target.value)}
                        >
                          <option value="fr-FR">Français (fr-FR)</option>
                          <option value="en-US">English (en-US)</option>
                          <option value="es-ES">Español (es-ES)</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label htmlFor="user_mail">
                          <FormattedMessage id="staff.mail" defaultMessage="Mail" /> <span style={{ color: "red" }}>*</span>
                        </label>
                        <input
                          type="email"
                          className="form-control"
                          id="user_mail"
                          required
                          value={user.user_mail}
                          onChange={handleInputChange}
                          name="user_mail"
                        />
                      </div>
                    </div>
                    <div onClick={() => { if (!hasValidEmail) setEmailHintVisible(true); }}>
                      <div className="form-group" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                        <input
                          type="checkbox"
                          id="send_invitation"
                          checked={sendInvitation}
                          disabled={!hasValidEmail}
                          onChange={(e) => setSendInvitation(e.target.checked)}
                        />
                        <label htmlFor="send_invitation" style={{ marginBottom: 0, cursor: !hasValidEmail ? 'not-allowed' : 'pointer' }}>
                          <FormattedMessage id="staff.send_invitation_mail" defaultMessage="Send welcome/signup email" />
                        </label>
                      </div>
                      {emailHintVisible && !hasValidEmail && (
                        <div style={{ color: '#dc3545', fontSize: '0.82em', textAlign: 'center', marginTop: -4 }}>
                          <FormattedMessage id="message.invalidMail" defaultMessage="Format email invalide" />
                        </div>
                      )}
                    </div>
                    <br />
                    <div>
                      <button onClick={props.cancelPopup} className="btn btn-danger">
                        <FormattedMessage id="button.cancel" defaultMessage="Cancel" />
                      </button>
                      <b>&emsp;</b>
                      <button onClick={saveUser} className="btn btn-success" disabled={!hasValidEmail || !firstName.trim() || !lastName.trim()}>
                        <FormattedMessage id="button.submit" defaultMessage="Submit" />
                      </button>
                    </div>
                  </div>
                )}
              </center>
            </div>
          )}
          <div>
            {invalidObject ? (
              <InvalidObject cancelPopup={cancelInvalidObjectPopup} message={message} />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default withStyles(Constants.styles)(AddUser);
